import React, { useEffect, useRef, useState, useMemo } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { sidebarNavigation } from "./MockSidebar"; // Import from our previous navigation file
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { useAppSelector } from "../../store/hooks";
import { selectUser, selectIsSuperAdmin } from "../../store/slices/authSlice";
import { canAccessMenuItem } from "../../utils/permissions";
// import logo from "../../images/logo/MCQWALE-LOGO-TM_CL.png";


interface SidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (arg: boolean) => void;
}

interface MenuItemProps {
  item: any;
  sidebarExpanded: boolean;
  user: any;
}

const MenuItem = ({ item, sidebarExpanded, user }: MenuItemProps) => {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);

  const hasChildren = item.children && item.children.length > 0;
  const location = useLocation();
  const path = location.pathname

  useEffect(() => {
    if (hasChildren) {
      setIsSubmenuOpen(item.children.map((child: any) => child.link).includes(path))
    }
  }, [path])

  const activeClasses = "bg-purLit12 text-white rounded-md";
  const normalClasses = "text-gray-700 hover:bg-gray-100 rounded-md";

  // Check if user can access this menu item
  const menuKey = item.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
  const hasAccess = canAccessMenuItem(user, menuKey);

  // If no access, don't render the item
  if (!hasAccess) {
    return null;
  }

  if (hasChildren) {
    // Filter children based on permissions
    const accessibleChildren = item.children.filter((child: any) => {
      const childKey = child.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
      return canAccessMenuItem(user, childKey);
    });

    // If no accessible children, don't render the parent
    if (accessibleChildren.length === 0) {
      return null;
    }

    return (
      <div className="relative">
        <div
          onClick={() => setIsSubmenuOpen(!isSubmenuOpen)}
          className={`cursor-pointer ${normalClasses}`}
        >
          <span className="flex items-center justify-between gap-2.5 p-2">
            <div className="flex items-center gap-2.5">
              {item.icon}
              {sidebarExpanded && <span>{item.name}</span>}
            </div>
            {sidebarExpanded && (
              <ChevronDown className={`w-4 h-4 transition-transform ${isSubmenuOpen ? 'rotate-180' : ''}`} />
            )}
          </span>
        </div>
        {isSubmenuOpen && sidebarExpanded && (
          <div className="ml-4 mt-1 space-y-1">
            {accessibleChildren.map((child: any) => (
              <NavLink
                key={child.name}
                to={child.link}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 p-2 ${isActive ? activeClasses : normalClasses}`
                }
              >
                {child.icon}
                <span>{child.name}</span>
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.link}
      end={item.link === "/"}
      className={({ isActive }) =>
        `flex items-center gap-2.5 p-2 ${isActive ? activeClasses : normalClasses}`
      }
    >
      {item.icon}
      {sidebarExpanded && <span>{item.name}</span>}
    </NavLink>
  );
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }: SidebarProps) => {
  const trigger = useRef<any>(null);
  const sidebar = useRef<any>(null);
  const user = useAppSelector(selectUser);
  const isSuperAdmin = useAppSelector(selectIsSuperAdmin);

  const storedSidebarExpanded = localStorage.getItem("sidebar-expanded");
  const [sidebarExpanded, setSidebarExpanded] = useState(
    storedSidebarExpanded === null ? true : storedSidebarExpanded === "true"
  );

  // Filter navigation items based on user permissions
  const filteredNavigation = useMemo(() => {
    if (!user) return [];

    return sidebarNavigation.map(section => ({
      ...section,
      items: section.items.filter(item => {
        const menuKey = item.name.toLowerCase().replace(/\s+/g, '-').replace(/&/g, '');
        const hasAccess = canAccessMenuItem(user, menuKey);

        // Filter out items that should only show for non-super admins
        if (item.showOnlyForNonSuperAdmin && isSuperAdmin) {
          return false;
        }

        return hasAccess;
      }).map(item => {
        // Also filter children if they exist
        if (item.children) {
          return {
            ...item,
            children: item.children.filter(child => {
              // Filter out child items that should only show for non-super admins
              if (child.showOnlyForNonSuperAdmin && isSuperAdmin) {
                return false;
              }
              return true;
            })
          };
        }
        return item;
      })
    })).filter(section => section.items.length > 0);
  }, [user, isSuperAdmin]);

  // Click outside handler
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!sidebar.current || !trigger.current) return;
      if (
        !sidebarOpen ||
        sidebar.current.contains(target) ||
        trigger.current.contains(target)
      )
        return;
      setSidebarOpen(false);
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  });

  // Escape key handler
  useEffect(() => {
    const keyHandler = (event: KeyboardEvent) => {
      if (!sidebarOpen || event.key !== 'Escape') return;
      setSidebarOpen(false);
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  });

  // Local storage and body class
  useEffect(() => {
    localStorage.setItem("sidebar-expanded", sidebarExpanded.toString());
    if (sidebarExpanded) {
      document.querySelector("body")?.classList.add("sidebar-expanded");
    } else {
      document.querySelector("body")?.classList.remove("sidebar-expanded");
    }
  }, [sidebarExpanded]);

  return (
    <aside
      ref={sidebar}
      className={`absolute left-0 top-0 z-10 shadow-lg h-screen flex flex-col 
        overflow-y-hidden bg-white duration-300 ease-linear dark:bg-gray-800 
        xl:static xl:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${sidebarExpanded ? "w-64" : "w-20"}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 shadow-md h-16">
        <NavLink to="/">
          {sidebarExpanded ? (
            <div className="flex items-center justify-center w-[230px]">
              <img className="w-[100px] " src="/logo192.png" alt="logo" />
            </div>
          ) : (
            <img className="w-[100px] " src="/logo192.png" alt="logo" />
          )}
        </NavLink>

        <button
          ref={trigger}
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-controls="sidebar"
          aria-expanded={sidebarOpen}
          className="block lg:hidden"
        >
          <ChevronLeft className="h-6 w-6" />
        </button>
      </div>

      {/* Navigation */}
      <div className="flex flex-col flex-1 overflow-y-auto">
        <nav className="mt-5 py-4 px-4 lg:mt-9 lg:px-6">
          {filteredNavigation.map((section) => (
            <div key={section.title} className="mb-4">
              <h3 className={`mb-1 ml-4  font-semibold text-fontColor ${!sidebarExpanded && "hidden"}`}>
                {section.title}
              </h3>
              <div className="space-y-2">
                {section.items.map((item) => (
                  <MenuItem
                    key={item.name}
                    item={item}
                    sidebarExpanded={sidebarExpanded}
                    user={user}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>
      </div>

      {/* Expand/Collapse Button */}
      <div className="flex justify-end p-4">
        <button
          onClick={() => setSidebarExpanded(!sidebarExpanded)}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          {sidebarExpanded ? (
            <ChevronLeft className="h-5 w-5" />
          ) : (
            <ChevronRight className="h-5 w-5" />
          )}
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
